"use client";

import JsBarcode from "jsbarcode";
import { useEffect, useMemo, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { Minus, Plus, Printer } from "lucide-react";
import type { ProductRow } from "@/lib/types/product";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  product: ProductRow | null;
};

const MAX_QTY = 200;
const MM_PER_INCH = 25.4;

type Unit = "mm" | "inch";

type LabelSize = {
  id: string;
  widthMm: number;
  heightMm: number;
  hint?: string;
  barcodeHeight: number;
  barcodeWidth: number;
  fontSize: number;
  nameClass: string;
  priceClass: string;
  mrpClass: string;
};

const MM_SIZES: LabelSize[] = [
  { id: "38x25", widthMm: 38, heightMm: 25, hint: "small thermal", barcodeHeight: 22, barcodeWidth: 0.9, fontSize: 8, nameClass: "text-[8px]", priceClass: "text-[10px]", mrpClass: "text-[7px]" },
  { id: "50x25", widthMm: 50, heightMm: 25, hint: "standard 1-up", barcodeHeight: 24, barcodeWidth: 1.0, fontSize: 9, nameClass: "text-[9px]", priceClass: "text-[11px]", mrpClass: "text-[8px]" },
  { id: "50x30", widthMm: 50, heightMm: 30, barcodeHeight: 28, barcodeWidth: 1.0, fontSize: 9, nameClass: "text-[9px]", priceClass: "text-[11px]", mrpClass: "text-[8px]" },
  { id: "58x40", widthMm: 58, heightMm: 40, hint: "POS receipt label", barcodeHeight: 38, barcodeWidth: 1.2, fontSize: 10, nameClass: "text-[10px]", priceClass: "text-[13px]", mrpClass: "text-[9px]" },
  { id: "75x50", widthMm: 75, heightMm: 50, hint: "large", barcodeHeight: 48, barcodeWidth: 1.4, fontSize: 11, nameClass: "text-[11px]", priceClass: "text-[14px]", mrpClass: "text-[10px]" },
  { id: "100x50", widthMm: 100, heightMm: 50, hint: "wide", barcodeHeight: 48, barcodeWidth: 1.6, fontSize: 12, nameClass: "text-[12px]", priceClass: "text-[16px]", mrpClass: "text-[10px]" },
];

const INCH_SIZES: (LabelSize & { widthIn: number; heightIn: number })[] = [
  { id: "1x0.5", widthIn: 1, heightIn: 0.5, widthMm: 1 * MM_PER_INCH, heightMm: 0.5 * MM_PER_INCH, hint: "small jewellery", barcodeHeight: 22, barcodeWidth: 0.9, fontSize: 8, nameClass: "text-[8px]", priceClass: "text-[10px]", mrpClass: "text-[7px]" },
  { id: "2x1", widthIn: 2, heightIn: 1, widthMm: 2 * MM_PER_INCH, heightMm: 1 * MM_PER_INCH, hint: "standard 1-up", barcodeHeight: 26, barcodeWidth: 1.0, fontSize: 9, nameClass: "text-[9px]", priceClass: "text-[12px]", mrpClass: "text-[8px]" },
  { id: "2.25x1.25", widthIn: 2.25, heightIn: 1.25, widthMm: 2.25 * MM_PER_INCH, heightMm: 1.25 * MM_PER_INCH, hint: "Dymo / Zebra common", barcodeHeight: 32, barcodeWidth: 1.1, fontSize: 10, nameClass: "text-[10px]", priceClass: "text-[12px]", mrpClass: "text-[9px]" },
  { id: "3x1", widthIn: 3, heightIn: 1, widthMm: 3 * MM_PER_INCH, heightMm: 1 * MM_PER_INCH, hint: "wide thin", barcodeHeight: 26, barcodeWidth: 1.2, fontSize: 10, nameClass: "text-[10px]", priceClass: "text-[12px]", mrpClass: "text-[9px]" },
  { id: "3x2", widthIn: 3, heightIn: 2, widthMm: 3 * MM_PER_INCH, heightMm: 2 * MM_PER_INCH, barcodeHeight: 48, barcodeWidth: 1.4, fontSize: 11, nameClass: "text-[11px]", priceClass: "text-[14px]", mrpClass: "text-[10px]" },
  { id: "4x2", widthIn: 4, heightIn: 2, widthMm: 4 * MM_PER_INCH, heightMm: 2 * MM_PER_INCH, hint: "shipping label", barcodeHeight: 50, barcodeWidth: 1.6, fontSize: 12, nameClass: "text-[12px]", priceClass: "text-[16px]", mrpClass: "text-[11px]" },
];

function fmtSize(s: LabelSize, unit: Unit): string {
  if (unit === "inch") {
    const inch = INCH_SIZES.find((x) => x.id === s.id);
    if (inch) return `${trim(inch.widthIn)} × ${trim(inch.heightIn)} in`;
    return `${trim(s.widthMm / MM_PER_INCH)} × ${trim(s.heightMm / MM_PER_INCH)} in`;
  }
  return `${trim(s.widthMm)} × ${trim(s.heightMm)} mm`;
}

function trim(n: number): string {
  return Number.isInteger(n) ? n.toFixed(0) : n.toFixed(2).replace(/\.?0+$/, "");
}

export function BarcodeModal({ open, onOpenChange, product }: Props) {
  const printRef = useRef<HTMLDivElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const [qty, setQty] = useState<number>(1);
  const [unit, setUnit] = useState<Unit>("mm");
  const [sizeId, setSizeId] = useState<string>("50x25");

  const sizes: LabelSize[] = unit === "mm" ? MM_SIZES : INCH_SIZES;
  const size = useMemo<LabelSize>(
    () => sizes.find((s) => s.id === sizeId) ?? sizes[1] ?? sizes[0],
    [sizes, sizeId],
  );

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: product?.name ? `labels-${product.name}` : "labels",
    pageStyle: `
      @page { size: ${size.widthMm}mm ${size.heightMm}mm; margin: 0; }
      @media print {
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .label { page-break-after: always; break-after: page; page-break-inside: avoid; break-inside: avoid; }
        .label:last-child { page-break-after: auto; break-after: auto; }
      }
    `,
  });

  useEffect(() => {
    if (open) {
      setQty(1);
      setUnit("mm");
      setSizeId("50x25");
    }
  }, [open]);

  // unit change → reset size to first option for that unit
  useEffect(() => {
    if (!sizes.find((s) => s.id === sizeId)) {
      setSizeId(sizes[1]?.id ?? sizes[0].id);
    }
  }, [unit, sizes, sizeId]);

  // render barcodes (preview + print sheet) — re-run on size/qty change
  useEffect(() => {
    if (!open || !product) return;

    const id = requestAnimationFrame(() => {
      const code = String(product.barcode || product.sku || product.id);
      const renderInto = (root: HTMLElement | null) => {
        if (!root) return;
        root.querySelectorAll<SVGElement>("svg.barcode-svg").forEach((svg) => {
          try {
            JsBarcode(svg, code, {
              format: "CODE128",
              width: size.barcodeWidth,
              height: size.barcodeHeight,
              displayValue: true,
              fontSize: size.fontSize,
              margin: 1,
            });
          } catch {
            /* ignore invalid symbology */
          }
        });
      };
      renderInto(previewRef.current);
      renderInto(printRef.current);
    });

    return () => cancelAnimationFrame(id);
  }, [open, product, qty, size]);

  const clampQty = (v: number) =>
    Math.max(1, Math.min(MAX_QTY, Math.floor(v) || 1));

  const labelStyle: React.CSSProperties = {
    width: `${size.widthMm}mm`,
    height: `${size.heightMm}mm`,
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Print barcode labels</DialogTitle>
        </DialogHeader>
        {product ? (
          <div className="space-y-4">
            {/* Unit toggle */}
            <div className="space-y-1.5">
              <Label className="text-sm">Units</Label>
              <div className="inline-flex rounded-lg border bg-muted/40 p-0.5">
                {(["mm", "inch"] as const).map((u) => (
                  <button
                    key={u}
                    type="button"
                    onClick={() => setUnit(u)}
                    className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                      unit === u
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {u === "mm" ? "Millimetre (mm)" : "Inch (in)"}
                  </button>
                ))}
              </div>
            </div>

            {/* Size selector — labels reflect chosen unit */}
            <div className="space-y-1.5">
              <Label className="text-sm">Label size</Label>
              <Select value={sizeId} onValueChange={(v) => setSizeId(v)}>
                <SelectTrigger className="h-9 w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sizes.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {fmtSize(s, unit)}
                      {s.hint ? ` — ${s.hint}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Quantity selector */}
            <div className="space-y-1.5">
              <Label htmlFor="bc-qty" className="text-sm">
                Number of labels
              </Label>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-9 w-9"
                  onClick={() => setQty((q) => clampQty(q - 1))}
                  disabled={qty <= 1}
                  aria-label="Decrease"
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <Input
                  id="bc-qty"
                  type="number"
                  min={1}
                  max={MAX_QTY}
                  value={qty}
                  onChange={(e) => setQty(clampQty(Number(e.target.value)))}
                  className="h-9 w-24 text-center font-mono text-base"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-9 w-9"
                  onClick={() => setQty((q) => clampQty(q + 1))}
                  disabled={qty >= MAX_QTY}
                  aria-label="Increase"
                >
                  <Plus className="h-4 w-4" />
                </Button>
                <div className="ml-2 flex flex-wrap gap-1">
                  {[5, 10, 20, 50].map((n) => (
                    <Button
                      key={n}
                      type="button"
                      size="sm"
                      variant={qty === n ? "default" : "outline"}
                      className="h-7 px-2 text-xs"
                      onClick={() => setQty(n)}
                    >
                      {n}
                    </Button>
                  ))}
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Max {MAX_QTY} labels per print.
              </p>
            </div>

            {/* Preview (single label, true mm size) */}
            <div className="space-y-1">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Preview ({fmtSize(size, unit)})
              </p>
              <div className="rounded-lg border bg-gray-50 p-3">
                <div
                  ref={previewRef}
                  className="label mx-auto box-border flex flex-col items-stretch overflow-hidden border border-dashed border-gray-300 bg-white p-1 text-black"
                  style={labelStyle}
                >
                  <svg className="barcode-svg mx-auto block max-w-full" />
                  <p className={`mt-0.5 line-clamp-2 text-center font-medium leading-tight ${size.nameClass}`}>
                    {product.name}
                  </p>
                  <p className={`text-center font-mono font-semibold ${size.priceClass}`}>
                    ₹{Number(product.selling_price).toFixed(2)}
                  </p>
                  <p className={`text-center font-mono text-gray-600 ${size.mrpClass}`}>
                    MRP ₹{Number(product.mrp).toFixed(2)}
                  </p>
                </div>
              </div>
            </div>

            {/* Hidden print sheet (qty copies) */}
            <div className="hidden">
              <div ref={printRef} className="bg-white text-black">
                {Array.from({ length: qty }).map((_, i) => (
                  <div
                    key={i}
                    className="label box-border flex flex-col items-stretch overflow-hidden p-1"
                    style={labelStyle}
                  >
                    <svg className="barcode-svg mx-auto block max-w-full" />
                    <p className={`mt-0.5 line-clamp-2 text-center font-medium leading-tight ${size.nameClass}`}>
                      {product.name}
                    </p>
                    <p className={`text-center font-mono font-semibold ${size.priceClass}`}>
                      ₹{Number(product.selling_price).toFixed(2)}
                    </p>
                    <p className={`text-center font-mono text-gray-600 ${size.mrpClass}`}>
                      MRP ₹{Number(product.mrp).toFixed(2)}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <Button type="button" className="w-full gap-2" onClick={() => handlePrint()}>
              <Printer className="h-4 w-4" />
              Print {qty} {qty === 1 ? "label" : "labels"} ({fmtSize(size, unit)})
            </Button>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
